import { Card } from './chunk-SWFXEBPS.js';
import { Popover, PopoverTrigger, PopoverContent, Skeleton } from './chunk-I4ZPXE6W.js';
import { Separator } from './chunk-NCCYLVD3.js';
import { Input } from './chunk-NHXI3UAE.js';
import { Badge } from './chunk-2WDEGOCV.js';
import { CONTROL_WIDTHS } from './chunk-JY5DSE5Q.js';
import { useFullscreenPortal } from './chunk-MBTLYAMU.js';
import { buttonVariants, Button } from './chunk-PLP233UC.js';
import { cn } from './chunk-FFX3LZW7.js';
import * as React13 from 'react';
import * as ProgressPrimitive from '@radix-ui/react-progress';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import * as SelectPrimitive from '@radix-ui/react-select';
import { ChevronDown, Check, Circle, ChevronRight, MoreHorizontal, ChevronLeft, CalendarIcon, Pipette, ExternalLink as ExternalLink$1, Minus, ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { useVirtualizer } from '@tanstack/react-virtual';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { cva } from 'class-variance-authority';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import * as SliderPrimitive from '@radix-ui/react-slider';
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import * as HoverCardPrimitive from '@radix-ui/react-hover-card';
import * as CollapsiblePrimitive from '@radix-ui/react-collapsible';
import { Slot } from '@radix-ui/react-slot';
import { DayPicker } from 'react-day-picker';
import { readableForeground, normalizeHex, isHexColor, CATALOG_PALETTE } from '@authe/fe-commons/color';
import { getTruncation, hexPrefixOf } from '@authe/fe-commons/display';
import { truncationKindOf } from '@authe/fe-commons/chains';

var Progress = React13.forwardRef(function Progress2({ className, value, max = 100, ...props }, ref) {
  const limit = Number.isFinite(max) && max > 0 ? max : 100;
  const current = typeof value === "number" && Number.isFinite(value) ? Math.min(limit, Math.max(0, value)) : null;
  const pct = (current ?? 0) / limit * 100;
  return /* @__PURE__ */ jsx(
    ProgressPrimitive.Root,
    {
      ref,
      value: current,
      max: limit,
      className: cn("relative h-2 w-full overflow-hidden rounded-full bg-secondary", className),
      ...props,
      children: /* @__PURE__ */ jsx(
        ProgressPrimitive.Indicator,
        {
          className: "size-full flex-1 bg-brand transition-transform",
          style: { transform: `translateX(-${100 - pct}%)` }
        }
      )
    }
  );
});
var Switch = React13.forwardRef(function Switch2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    SwitchPrimitive.Root,
    {
      ref,
      className: cn(
        "peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-brand data-[state=unchecked]:bg-input focus-ring",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx(
        SwitchPrimitive.Thumb,
        {
          className: cn(
            "pointer-events-none block size-4 rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0"
          )
        }
      )
    }
  );
});
function ViewModeToggle({
  options,
  value,
  onChange,
  size = "md",
  className,
  ...rest
}) {
  const buttons = React13.useRef(/* @__PURE__ */ new Map());
  const enabled = options.filter((option) => !option.disabled);
  const tabStop = enabled.some((option) => option.value === value) ? value : enabled[0]?.value;
  const pad = size === "sm" ? "h-7 px-2 text-xs" : "h-8 px-2.5 text-sm";
  return /* @__PURE__ */ jsx(
    "div",
    {
      role: "radiogroup",
      "aria-label": rest["aria-label"],
      className: cn("inline-flex items-center gap-1 rounded-lg bg-muted p-1 text-muted-foreground", className),
      children: options.map((option) => {
        const active = option.value === value;
        return /* @__PURE__ */ jsxs(
          "button",
          {
            ref: (element) => {
              if (element) buttons.current.set(option.value, element);
              else buttons.current.delete(option.value);
            },
            type: "button",
            tabIndex: option.value === tabStop ? 0 : -1,
            onKeyDown: (event) => {
              if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"].includes(event.key) || !enabled.length) return;
              event.preventDefault();
              const current = enabled.findIndex((item) => item.value === option.value);
              const next = event.key === "Home" ? 0 : event.key === "End" ? enabled.length - 1 : (current + (event.key === "ArrowLeft" || event.key === "ArrowUp" ? -1 : 1) + enabled.length) % enabled.length;
              const target = enabled[next];
              onChange(target.value);
              buttons.current.get(target.value)?.focus();
            },
            role: "radio",
            "aria-checked": active,
            disabled: option.disabled,
            title: option.title,
            onClick: () => !active && !option.disabled && onChange(option.value),
            className: cn(
              "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md font-medium ring-offset-background transition-all",
              "disabled:pointer-events-none disabled:opacity-50 focus-ring",
              pad,
              active ? "bg-background text-foreground shadow-sm" : "hover:text-foreground"
            ),
            children: [
              option.icon,
              option.label
            ]
          },
          option.value
        );
      })
    }
  );
}
function FilterChip({
  active,
  onClick,
  count,
  countUnknown = false,
  disabled = false,
  title,
  keepFocus = false,
  children,
  className
}) {
  return /* @__PURE__ */ jsxs(
    "button",
    {
      type: "button",
      "aria-pressed": active,
      disabled,
      title,
      onMouseDown: keepFocus ? (e) => e.preventDefault() : void 0,
      onClick,
      className: cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors",
        "disabled:cursor-not-allowed disabled:opacity-50",
        active ? "border-primary bg-primary/10 font-medium text-primary" : "border-border text-muted-foreground hover:bg-accent hover:text-foreground",
        className
      ),
      children: [
        children,
        count != null ? /* @__PURE__ */ jsx("span", { className: "tabular-nums", children: count.toLocaleString() }) : countUnknown ? (
          // Not `aria-hidden`: a screen reader that skipped it would be back to
          // the silence this exists to break. `title` carries the reason for
          // both, which is why this branch is only reachable with one.
          /* @__PURE__ */ jsx("span", { className: "tabular-nums", children: "\u2014" })
        ) : null
      ]
    }
  );
}
function Select({
  onValueChange,
  ...props
}) {
  const guarded = React13.useMemo(
    () => onValueChange ? (next) => {
      if (next !== "") onValueChange(next);
    } : void 0,
    [onValueChange]
  );
  return /* @__PURE__ */ jsx(SelectPrimitive.Root, { ...props, onValueChange: guarded });
}
var SelectGroup = SelectPrimitive.Group;
var SelectValue = SelectPrimitive.Value;
var SelectLabel = React13.forwardRef(function SelectLabel2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    SelectPrimitive.Label,
    {
      ref,
      className: cn("px-2 py-1.5 text-xs font-medium text-muted-foreground", className),
      ...props
    }
  );
});
var SelectTrigger = React13.forwardRef(function SelectTrigger2({ className, children, width = "full", size = "default", ...props }, ref) {
  return /* @__PURE__ */ jsxs(
    SelectPrimitive.Trigger,
    {
      ref,
      className: cn(
        // `[&>span]:min-w-0 truncate`, not `line-clamp-1`: a line clamp cannot
        // shorten a `whitespace-nowrap` label in a flex row, so a long value ran
        // under the chevron (the Team page's switcher, on any long team name).
        "flex h-9 w-full items-center justify-between gap-2 whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 [&>span]:min-w-0 [&>span]:truncate focus-ring",
        CONTROL_WIDTHS[width],
        size === "sm" && "h-8",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx(SelectPrimitive.Icon, { asChild: true, children: /* @__PURE__ */ jsx(ChevronDown, { className: "size-4 opacity-50" }) })
      ]
    }
  );
});
var SelectContent = React13.forwardRef(function SelectContent2({ className, children, position = "popper", ...props }, ref) {
  const portalContainer = useFullscreenPortal();
  return /* @__PURE__ */ jsx(SelectPrimitive.Portal, { container: portalContainer, children: /* @__PURE__ */ jsx(
    SelectPrimitive.Content,
    {
      ref,
      position,
      className: cn(
        // `min(...)` of the two caps, not the fixed 24rem alone: that cap is a
        // taste limit ("a select should not be half the screen") and says
        // nothing about whether there is 24rem of room. On a short window, or
        // a select opened near the bottom, the list overran the viewport and
        // `overflow-hidden` clipped the rest away. The available height is the
        // room Radix actually measured, so whichever is smaller wins.
        "relative z-50 max-h-[min(24rem,var(--radix-select-content-available-height))] min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out",
        position === "popper" && "data-[side=bottom]:translate-y-1",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx(
        SelectPrimitive.Viewport,
        {
          className: cn("p-1", position === "popper" && "w-full min-w-[var(--radix-select-trigger-width)]"),
          children
        }
      )
    }
  ) });
});
var SelectItem = React13.forwardRef(function SelectItem2({ className, children, ...props }, ref) {
  return /* @__PURE__ */ jsxs(
    SelectPrimitive.Item,
    {
      ref,
      className: cn(
        "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-hidden focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx("span", { className: "absolute right-2 flex size-3.5 items-center justify-center", children: /* @__PURE__ */ jsx(SelectPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx(Check, { className: "size-4" }) }) }),
        /* @__PURE__ */ jsx(SelectPrimitive.ItemText, { children })
      ]
    }
  );
});
var EMPTY_SENTINEL = "__empty__";
function sections(options) {
  const out = [];
  for (const option of options) {
    const last = out[out.length - 1];
    if (last && last.group === option.group) last.options.push(option);
    else out.push({ group: option.group, options: [option] });
  }
  return out;
}
function EnumSelect({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  id,
  className,
  contentClassName,
  "aria-label": ariaLabel,
  title
}) {
  const offersEmpty = options.some((option) => option.value === "");
  const selectValue = value === "" ? offersEmpty ? EMPTY_SENTINEL : void 0 : value || void 0;
  const handleChange = (next) => onChange(next === EMPTY_SENTINEL ? "" : next);
  return /* @__PURE__ */ jsxs(Select, { value: selectValue, onValueChange: handleChange, disabled, children: [
    /* @__PURE__ */ jsx(SelectTrigger, { id, className, "aria-label": ariaLabel, title, children: /* @__PURE__ */ jsx(SelectValue, { placeholder }) }),
    /* @__PURE__ */ jsx(SelectContent, { className: contentClassName, children: sections(options).map((section, index) => {
      const items = section.options.map((option) => /* @__PURE__ */ jsx(
        SelectItem,
        {
          value: option.value === "" ? EMPTY_SENTINEL : option.value,
          disabled: option.disabled,
          children: option.label
        },
        option.value || EMPTY_SENTINEL
      ));
      return section.group === void 0 ? /* @__PURE__ */ jsx(React13.Fragment, { children: items }, `ungrouped-${index}`) : /* @__PURE__ */ jsxs(SelectGroup, { children: [
        /* @__PURE__ */ jsx(SelectLabel, { children: section.group }),
        items
      ] }, section.group);
    }) })
  ] });
}
function scrollParentOf(node) {
  let el = node?.parentElement ?? null;
  while (el) {
    const overflow = getComputedStyle(el).overflowY;
    if (overflow === "auto" || overflow === "scroll") return el;
    el = el.parentElement;
  }
  return document.scrollingElement ?? null;
}
function VirtualList({
  items,
  getKey,
  renderItem,
  estimateSize = 76,
  overscan = 8,
  virtualizeAbove = 60,
  className,
  "aria-label": ariaLabel
}) {
  const listRef = React13.useRef(null);
  const [scrollEl, setScrollEl] = React13.useState(null);
  const [metrics, setMetrics] = React13.useState({ scrollMargin: 0, viewport: 0 });
  React13.useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const parent = scrollParentOf(list);
    setScrollEl(parent);
    const measure = () => {
      if (!listRef.current || !parent) return;
      const top = listRef.current.getBoundingClientRect().top;
      const parentTop = parent === document.scrollingElement ? 0 : parent.getBoundingClientRect().top;
      setMetrics({
        scrollMargin: top - parentTop + (parent.scrollTop ?? 0),
        // `offsetHeight`, because that is exactly what the virtualizer measures
        // the scroller with (`virtual-core`'s `getRect`). Reading a different
        // box lets "we have a viewport" and "the virtualizer can compute a
        // range" disagree, and that disagreement renders a list of nothing.
        viewport: parent.offsetHeight || parent.clientHeight || 0
      });
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    observer.observe(parent ?? list);
    return () => observer.disconnect();
  }, [items.length]);
  const windowed = metrics.viewport > 0 && items.length > virtualizeAbove;
  const virtualizer = useVirtualizer({
    count: windowed ? items.length : 0,
    getScrollElement: () => scrollEl,
    estimateSize: () => estimateSize,
    overscan,
    scrollMargin: metrics.scrollMargin
  });
  if (!windowed) {
    return /* @__PURE__ */ jsx("ul", { ref: listRef, "aria-label": ariaLabel, className: cn("w-full", className), children: items.map((item, index) => /* @__PURE__ */ jsx("li", { children: renderItem(item, index) }, getKey(item, index))) });
  }
  return /* @__PURE__ */ jsx(
    "ul",
    {
      ref: listRef,
      "aria-label": ariaLabel,
      className: cn("relative w-full", className),
      style: { height: virtualizer.getTotalSize() },
      children: virtualizer.getVirtualItems().map((virtualItem) => {
        const item = items[virtualItem.index];
        if (item === void 0) return null;
        return /* @__PURE__ */ jsx(
          "li",
          {
            "data-index": virtualItem.index,
            ref: virtualizer.measureElement,
            "aria-setsize": items.length,
            "aria-posinset": virtualItem.index + 1,
            className: "absolute left-0 top-0 w-full",
            style: { transform: `translateY(${virtualItem.start - metrics.scrollMargin}px)` },
            children: renderItem(item, virtualItem.index)
          },
          getKey(item, virtualItem.index)
        );
      })
    }
  );
}
var Accordion = AccordionPrimitive.Root;
var AccordionItem = React13.forwardRef(function AccordionItem2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(AccordionPrimitive.Item, { ref, className: cn("border-b", className), ...props });
});
var AccordionTrigger = React13.forwardRef(function AccordionTrigger2({ className, children, ...props }, ref) {
  return /* @__PURE__ */ jsx(AccordionPrimitive.Header, { className: "flex", children: /* @__PURE__ */ jsxs(
    AccordionPrimitive.Trigger,
    {
      ref,
      className: cn(
        "flex flex-1 items-center justify-between py-4 text-sm font-medium transition-all hover:underline [&[data-state=open]>svg]:rotate-180",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx(ChevronDown, { className: "size-4 shrink-0 text-muted-foreground transition-transform duration-200" })
      ]
    }
  ) });
});
var AccordionContent = React13.forwardRef(function AccordionContent2({ className, children, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    AccordionPrimitive.Content,
    {
      ref,
      className: "overflow-hidden text-sm data-[state=closed]:animate-out data-[state=open]:animate-in",
      ...props,
      children: /* @__PURE__ */ jsx("div", { className: cn("pb-4 pt-0", className), children })
    }
  );
});
var SIZES = {
  sm: "size-6 text-3xs",
  md: "size-8 text-xs",
  lg: "size-10 text-sm",
  xl: "size-14 text-base"
};
function Avatar({ src, alt, fallback, size = "md", className }) {
  const [failedSrc, setFailedSrc] = React13.useState();
  const showImage = src && failedSrc !== src;
  return /* @__PURE__ */ jsx(
    "span",
    {
      className: cn(
        "inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted font-semibold text-muted-foreground",
        SIZES[size],
        className
      ),
      children: showImage ? /* @__PURE__ */ jsx(
        "img",
        {
          src,
          alt: alt ?? "",
          className: "block size-full object-cover",
          onError: () => setFailedSrc(src)
        }
      ) : fallback ?? "?"
    }
  );
}
var alertVariants = cva(
  "relative w-full rounded-lg border p-4 [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-current [&>svg~*]:pl-7",
  {
    variants: {
      variant: {
        default: "bg-background text-foreground",
        destructive: "border-destructive/40 bg-destructive/5 text-destructive",
        // The interface-state tokens, not a risk band or a finality state
        // borrowed for the colour: a warning here is not a medium-risk finding.
        warning: "border-warning/40 bg-warning/5 text-warning",
        success: "border-success/40 bg-success/5 text-success",
        info: "border-info/40 bg-info/5 text-info"
      }
    },
    defaultVariants: { variant: "default" }
  }
);
var Alert = React13.forwardRef(function Alert2({ className, variant, ...props }, ref) {
  return /* @__PURE__ */ jsx("div", { ref, role: "alert", className: cn(alertVariants({ variant }), className), ...props });
});
var AlertTitle = React13.forwardRef(
  function AlertTitle2({ className, ...props }, ref) {
    return /* @__PURE__ */ jsx("h5", { ref, className: cn("mb-1 font-medium leading-none tracking-tight", className), ...props });
  }
);
var AlertDescription = React13.forwardRef(
  function AlertDescription2({ className, ...props }, ref) {
    return /* @__PURE__ */ jsx("div", { ref, className: cn("text-sm [&_p]:leading-relaxed", className), ...props });
  }
);
var RadioGroup = React13.forwardRef(function RadioGroup2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(RadioGroupPrimitive.Root, { ref, className: cn("grid gap-2", className), ...props });
});
var RadioGroupItem = React13.forwardRef(function RadioGroupItem2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    RadioGroupPrimitive.Item,
    {
      ref,
      className: cn(
        "aspect-square size-4 rounded-full border border-brand text-brand disabled:cursor-not-allowed disabled:opacity-50 focus-ring",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx(RadioGroupPrimitive.Indicator, { className: "flex items-center justify-center", children: /* @__PURE__ */ jsx(Circle, { className: "size-2.5 fill-current text-current" }) })
    }
  );
});
var Slider = React13.forwardRef(function Slider2({ className, thumbLabels, getValueText, ...props }, ref) {
  const [internal, setInternal] = React13.useState(props.defaultValue ?? [props.min ?? 0]);
  const values = props.value ?? internal;
  const thumbCount = Array.isArray(props.value) ? props.value.length : Array.isArray(props.defaultValue) ? props.defaultValue.length : 1;
  return /* @__PURE__ */ jsxs(
    SliderPrimitive.Root,
    {
      ref,
      className: cn("relative flex w-full touch-none select-none items-center", className),
      ...props,
      onValueChange: (value) => {
        setInternal(value);
        props.onValueChange?.(value);
      },
      children: [
        /* @__PURE__ */ jsx(SliderPrimitive.Track, { className: "relative h-1.5 w-full grow overflow-hidden rounded-full bg-secondary", children: /* @__PURE__ */ jsx(SliderPrimitive.Range, { className: "absolute h-full bg-brand" }) }),
        Array.from({ length: thumbCount }).map((_, i) => /* @__PURE__ */ jsx(
          SliderPrimitive.Thumb,
          {
            "aria-label": thumbLabels?.[i],
            "aria-valuetext": getValueText && values[i] != null ? getValueText(values[i], i) : void 0,
            className: "block size-6 rounded-full border border-brand/50 bg-background shadow-sm transition-colors disabled:pointer-events-none disabled:opacity-50 focus-ring"
          },
          i
        ))
      ]
    }
  );
});
var ScrollBar = React13.forwardRef(function ScrollBar2({ className, orientation = "vertical", ...props }, ref) {
  return /* @__PURE__ */ jsx(
    ScrollAreaPrimitive.ScrollAreaScrollbar,
    {
      ref,
      orientation,
      className: cn(
        "flex touch-none select-none transition-colors",
        orientation === "vertical" && "h-full w-2.5 border-l border-l-transparent p-[1px]",
        orientation === "horizontal" && "h-2.5 flex-col border-t border-t-transparent p-[1px]",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx(ScrollAreaPrimitive.ScrollAreaThumb, { className: "relative flex-1 rounded-full bg-border" })
    }
  );
});
var ScrollArea = React13.forwardRef(function ScrollArea2({ className, children, ...props }, ref) {
  return /* @__PURE__ */ jsxs(ScrollAreaPrimitive.Root, { ref, className: cn("relative overflow-hidden", className), ...props, children: [
    /* @__PURE__ */ jsx(ScrollAreaPrimitive.Viewport, { className: "size-full rounded-[inherit] [&>div]:!block", style: { maxHeight: "inherit" }, children }),
    /* @__PURE__ */ jsx(ScrollBar, {}),
    /* @__PURE__ */ jsx(ScrollAreaPrimitive.Corner, {})
  ] });
});
var AlertDialog = AlertDialogPrimitive.Root;
var AlertDialogTrigger = AlertDialogPrimitive.Trigger;
function AlertDialogPortal({ container, ...props }) {
  const fullscreen = useFullscreenPortal();
  return /* @__PURE__ */ jsx(AlertDialogPrimitive.Portal, { container: container === void 0 ? fullscreen : container, ...props });
}
var AlertDialogOverlay = React13.forwardRef(function AlertDialogOverlay2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    AlertDialogPrimitive.Overlay,
    {
      ref,
      className: cn(
        "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className
      ),
      ...props
    }
  );
});
var AlertDialogContent = React13.forwardRef(function AlertDialogContent2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsxs(AlertDialogPortal, { children: [
    /* @__PURE__ */ jsx(AlertDialogOverlay, {}),
    /* @__PURE__ */ jsx(
      AlertDialogPrimitive.Content,
      {
        ref,
        className: cn(
          "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out sm:rounded-lg",
          className
        ),
        ...props
      }
    )
  ] });
});
function AlertDialogHeader({ className, ...props }) {
  return /* @__PURE__ */ jsx("div", { className: cn("flex flex-col gap-y-2 text-center sm:text-left", className), ...props });
}
function AlertDialogFooter({ className, ...props }) {
  return /* @__PURE__ */ jsx("div", { className: cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className), ...props });
}
var AlertDialogTitle = React13.forwardRef(function AlertDialogTitle2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(AlertDialogPrimitive.Title, { ref, className: cn("text-lg font-semibold", className), ...props });
});
var AlertDialogDescription = React13.forwardRef(function AlertDialogDescription2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(AlertDialogPrimitive.Description, { ref, className: cn("text-sm text-muted-foreground", className), ...props });
});
var AlertDialogAction = React13.forwardRef(function AlertDialogAction2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(AlertDialogPrimitive.Action, { ref, className: cn(buttonVariants(), className), ...props });
});
var AlertDialogCancel = React13.forwardRef(function AlertDialogCancel2({ className, ...props }, ref) {
  return /* @__PURE__ */ jsx(
    AlertDialogPrimitive.Cancel,
    {
      ref,
      className: cn(buttonVariants({ variant: "outline" }), "mt-2 sm:mt-0", className),
      ...props
    }
  );
});
var HoverCard = HoverCardPrimitive.Root;
var HoverCardTrigger = HoverCardPrimitive.Trigger;
var HoverCardContent = React13.forwardRef(function HoverCardContent2({ className, align = "center", sideOffset = 4, ...props }, ref) {
  return /* @__PURE__ */ jsx(HoverCardPrimitive.Portal, { children: /* @__PURE__ */ jsx(
    HoverCardPrimitive.Content,
    {
      ref,
      align,
      sideOffset,
      className: cn(
        "z-50 w-64 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-hidden data-[state=open]:animate-in data-[state=closed]:animate-out",
        className
      ),
      ...props
    }
  ) });
});
var Collapsible = CollapsiblePrimitive.Root;
var CollapsibleTrigger2 = CollapsiblePrimitive.CollapsibleTrigger;
var CollapsibleContent2 = CollapsiblePrimitive.CollapsibleContent;
var Breadcrumb = React13.forwardRef(function Breadcrumb2({ ...props }, ref) {
  return /* @__PURE__ */ jsx("nav", { ref, "aria-label": "breadcrumb", ...props });
});
var BreadcrumbList = React13.forwardRef(
  function BreadcrumbList2({ className, ...props }, ref) {
    return /* @__PURE__ */ jsx(
      "ol",
      {
        ref,
        className: cn(
          "flex flex-wrap items-center gap-1.5 break-words text-sm text-muted-foreground sm:gap-2.5",
          className
        ),
        ...props
      }
    );
  }
);
var BreadcrumbItem = React13.forwardRef(
  function BreadcrumbItem2({ className, ...props }, ref) {
    return /* @__PURE__ */ jsx("li", { ref, className: cn("inline-flex items-center gap-1.5", className), ...props });
  }
);
var BreadcrumbLink = React13.forwardRef(function BreadcrumbLink2({ asChild, className, ...props }, ref) {
  const Comp = asChild ? Slot : "a";
  return /* @__PURE__ */ jsx(Comp, { ref, className: cn("transition-colors hover:text-foreground", className), ...props });
});
var BreadcrumbPage = React13.forwardRef(
  function BreadcrumbPage2({ className, ...props }, ref) {
    return /* @__PURE__ */ jsx(
      "span",
      {
        ref,
        role: "link",
        "aria-disabled": "true",
        "aria-current": "page",
        className: cn("font-normal text-foreground", className),
        ...props
      }
    );
  }
);
function BreadcrumbSeparator({
  children,
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx("li", { role: "presentation", "aria-hidden": "true", className: cn("[&>svg]:h-3.5 [&>svg]:w-3.5", className), ...props, children: children ?? /* @__PURE__ */ jsx(ChevronRight, {}) });
}
function BreadcrumbEllipsis({ className, ...props }) {
  return /* @__PURE__ */ jsxs(
    "span",
    {
      role: "presentation",
      "aria-hidden": "true",
      className: cn("flex size-9 items-center justify-center", className),
      ...props,
      children: [
        /* @__PURE__ */ jsx(MoreHorizontal, { className: "size-4" }),
        /* @__PURE__ */ jsx("span", { className: "sr-only", children: "More" })
      ]
    }
  );
}
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}) {
  return /* @__PURE__ */ jsx(
    DayPicker,
    {
      showOutsideDays,
      className: cn("p-3", className),
      classNames: {
        months: "flex flex-col sm:flex-row gap-4 relative",
        month: "space-y-4",
        month_caption: "flex justify-center pt-1 relative items-center h-7",
        caption_label: "text-sm font-medium",
        // `z-10` is load-bearing, not decoration. react-day-picker renders `Nav`
        // BEFORE the months (see its `DayPicker` render: Months > Nav > Month >
        // MonthCaption), and `month_caption` below is `relative`. Two positioned
        // elements with `z-index: auto` paint in DOM order, so the captions
        // painted over this absolutely-positioned bar and swallowed every click
        // on the previous/next buttons — the arrows rendered, highlighted on
        // hover, and did nothing.
        nav: "flex items-center gap-1 absolute inset-x-1 top-0 z-10 justify-between",
        button_previous: cn(
          buttonVariants({ variant: "outline" }),
          "size-7 bg-transparent p-0 opacity-50 hover:opacity-100"
        ),
        button_next: cn(
          buttonVariants({ variant: "outline" }),
          "size-7 bg-transparent p-0 opacity-50 hover:opacity-100"
        ),
        month_grid: "w-full border-collapse space-y-1",
        weekdays: "flex",
        weekday: "text-muted-foreground rounded-md w-9 font-normal text-xs",
        week: "flex w-full mt-2",
        day: "size-9 text-center text-sm p-0 relative [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
        day_button: cn(buttonVariants({ variant: "ghost" }), "size-9 p-0 font-normal aria-selected:opacity-100"),
        range_end: "day-range-end",
        selected: "bg-brand text-brand-foreground hover:bg-brand hover:text-brand-foreground focus:bg-brand focus:text-brand-foreground",
        today: "bg-accent text-accent-foreground",
        outside: "day-outside text-muted-foreground opacity-50",
        disabled: "text-muted-foreground opacity-50",
        range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
        hidden: "invisible",
        ...classNames
      },
      components: {
        Chevron: ({ orientation, className: chevronClassName, ...chevronProps }) => {
          const Icon2 = orientation === "left" ? ChevronLeft : ChevronRight;
          return /* @__PURE__ */ jsx(Icon2, { className: cn("size-4", chevronClassName), ...chevronProps });
        }
      },
      ...props
    }
  );
}
var DEFAULT_FORMAT = new Intl.DateTimeFormat(void 0, {
  year: "numeric",
  month: "short",
  day: "numeric"
});
function DatePicker({
  value,
  defaultValue,
  onChange,
  placeholder = "Pick a date",
  disabled,
  className,
  formatLabel
}) {
  const [open, setOpen] = React13.useState(false);
  const [internal, setInternal] = React13.useState(defaultValue);
  const isControlled = value !== void 0 || onChange !== void 0;
  const selected = isControlled ? value : internal;
  const handleSelect = (date) => {
    if (!isControlled) setInternal(date);
    onChange?.(date);
    if (date) setOpen(false);
  };
  const label = selected ? (formatLabel ?? ((d) => DEFAULT_FORMAT.format(d)))(selected) : placeholder;
  return /* @__PURE__ */ jsxs(Popover, { open, onOpenChange: setOpen, children: [
    /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(
      Button,
      {
        variant: "outline",
        className: cn("w-full justify-start text-left font-normal", !selected && "text-muted-foreground", className),
        children: [
          /* @__PURE__ */ jsx(CalendarIcon, { className: "mr-2 size-4" }),
          label
        ]
      }
    ) }),
    /* @__PURE__ */ jsx(PopoverContent, { className: "w-auto p-0", align: "start", children: /* @__PURE__ */ jsx(Calendar, { mode: "single", selected, onSelect: handleSelect, disabled, autoFocus: true }) })
  ] });
}
var DATE_FMT = new Intl.DateTimeFormat(void 0, { month: "short", day: "numeric" });
var DATETIME_FMT = new Intl.DateTimeFormat(void 0, {
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit"
});
function startOfDay(date) {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}
function endOfDay(date) {
  const next = new Date(date);
  next.setHours(23, 59, 59, 999);
  return next;
}
function isWholeDayRange(from, to) {
  const startsAtMidnight = !from || from.getTime() === startOfDay(from).getTime();
  const endsAtMidnight = !to || to.getTime() === endOfDay(to).getTime();
  return startsAtMidnight && endsAtMidnight;
}
function timeInputValue(date) {
  if (!date) return "";
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}
function withTimeOfDay(date, value, edge) {
  const parts = value.split(":");
  const asMinute = (raw, max) => {
    const parsed = Number.parseInt(raw ?? "", 10);
    if (!Number.isFinite(parsed)) return 0;
    return Math.min(Math.max(parsed, 0), max);
  };
  const next = new Date(date);
  next.setHours(
    asMinute(parts[0], 23),
    asMinute(parts[1], 59),
    edge === "end" ? 59 : 0,
    edge === "end" ? 999 : 0
  );
  return next;
}
function absoluteRangeFrom(picked, options = {}) {
  const { showTime = false, keepTimesFrom } = options;
  const carry = showTime ? keepTimesFrom : void 0;
  return {
    from: picked?.from ? carry?.from ? withTimeOfDay(picked.from, timeInputValue(carry.from), "start") : startOfDay(picked.from) : void 0,
    to: picked?.to ? carry?.to ? withTimeOfDay(picked.to, timeInputValue(carry.to), "end") : endOfDay(picked.to) : void 0
  };
}
var defaultRangeLabel = (from, to) => isWholeDayRange(from, to) ? `${DATE_FMT.format(from)} \u2013 ${DATE_FMT.format(to)}` : `${DATETIME_FMT.format(from)} \u2013 ${DATETIME_FMT.format(to)}`;
function DateRangePicker({
  value,
  defaultValue,
  onChange,
  presets,
  placeholder = "Select range",
  align = "start",
  numberOfMonths = 2,
  showTime = false,
  disabled,
  latest,
  className,
  formatRange = defaultRangeLabel
}) {
  const [open, setOpen] = React13.useState(false);
  const [internal, setInternal] = React13.useState(defaultValue ?? {});
  const isControlled = value !== void 0 || onChange !== void 0;
  const current = isControlled ? value ?? {} : internal;
  const commit = (next) => {
    if (!isControlled) setInternal(next);
    onChange?.(next);
  };
  const selectPreset = (presetId) => {
    commit({ presetId });
    setOpen(false);
  };
  const selectRange = (range) => {
    const next = absoluteRangeFrom(range, { showTime, keepTimesFrom: current });
    commit(next);
    if (next.from && next.to && !showTime) setOpen(false);
  };
  const setBoundTime = (edge, time) => {
    if (!time) return;
    const anchor = edge === "start" ? current.from : current.to;
    if (!anchor) return;
    const stamped = withTimeOfDay(anchor, time, edge);
    commit(edge === "start" ? { ...current, from: stamped } : { ...current, to: stamped });
  };
  const activePreset = presets?.find((p) => p.id === current.presetId);
  const label = activePreset ? activePreset.label : current.from && current.to ? formatRange(current.from, current.to) : current.from ? DATE_FMT.format(current.from) : placeholder;
  const calendarSelected = current.from || current.to ? { from: current.from, to: current.to } : void 0;
  return /* @__PURE__ */ jsxs(Popover, { open, onOpenChange: setOpen, children: [
    /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(
      Button,
      {
        variant: "outline",
        className: cn(
          "justify-start text-left font-normal",
          !activePreset && !current.from && "text-muted-foreground",
          className
        ),
        children: [
          /* @__PURE__ */ jsx(CalendarIcon, { className: "mr-2 size-4" }),
          label
        ]
      }
    ) }),
    /* @__PURE__ */ jsxs(PopoverContent, { className: "flex w-auto p-0", align, children: [
      presets && presets.length > 0 ? /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-1 p-2", role: "listbox", "aria-label": "Quick ranges", children: presets.map((preset) => /* @__PURE__ */ jsx(
          Button,
          {
            variant: preset.id === current.presetId ? "secondary" : "ghost",
            size: "sm",
            className: "justify-start font-normal",
            role: "option",
            "aria-selected": preset.id === current.presetId,
            onClick: () => selectPreset(preset.id),
            children: preset.label
          },
          preset.id
        )) }),
        /* @__PURE__ */ jsx(Separator, { orientation: "vertical", className: "h-auto" })
      ] }) : null,
      /* @__PURE__ */ jsxs("div", { className: "flex flex-col", children: [
        /* @__PURE__ */ jsx(
          Calendar,
          {
            mode: "range",
            selected: calendarSelected,
            onSelect: selectRange,
            numberOfMonths,
            disabled: latest ? [...disabled === void 0 ? [] : [disabled].flat(), { after: latest }] : disabled,
            endMonth: latest,
            autoFocus: true
          }
        ),
        showTime ? /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(Separator, {}),
          /* @__PURE__ */ jsxs("div", { className: "space-y-1.5 p-3", children: [
            /* @__PURE__ */ jsxs("div", { className: "flex items-end gap-3", children: [
              /* @__PURE__ */ jsx(
                TimeField,
                {
                  label: "From",
                  value: timeInputValue(current.from),
                  onChange: (time) => setBoundTime("start", time),
                  disabled: !current.from
                }
              ),
              /* @__PURE__ */ jsx(
                TimeField,
                {
                  label: "To",
                  value: timeInputValue(current.to),
                  onChange: (time) => setBoundTime("end", time),
                  disabled: !current.to
                }
              )
            ] }),
            !current.from || !current.to ? /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "Pick a start and end day above to set times." }) : null
          ] })
        ] }) : null
      ] })
    ] })
  ] });
}
function TimeField({
  label,
  value,
  onChange,
  disabled
}) {
  return /* @__PURE__ */ jsxs("label", { className: "flex flex-1 flex-col gap-1 text-xs text-muted-foreground", children: [
    label,
    /* @__PURE__ */ jsx(
      "input",
      {
        type: "time",
        value,
        disabled,
        onChange: (event) => onChange(event.target.value),
        className: cn(
          "h-8 rounded-md border border-input bg-background px-2 text-sm text-foreground",
          "focus-ring",
          "disabled:cursor-not-allowed disabled:opacity-50"
        )
      }
    )
  ] });
}
var SWATCH_SIZE = {
  sm: "size-5",
  md: "size-7",
  lg: "size-9"
};
function ColorSwatch({
  color,
  selected,
  onSelect,
  size = "md",
  title,
  className
}) {
  const fg = readableForeground(color);
  const Comp = onSelect ? "button" : "span";
  return /* @__PURE__ */ jsx(
    Comp,
    {
      ...onSelect ? { type: "button", onClick: onSelect } : {},
      title: title ?? color,
      "aria-label": title ?? color,
      "aria-pressed": onSelect ? Boolean(selected) : void 0,
      className: cn(
        "inline-flex items-center justify-center rounded-md border border-black/10 shadow-xs transition-transform",
        onSelect && "cursor-pointer hover:scale-105 focus-ring",
        selected && "ring-2 ring-ring ring-offset-1",
        SWATCH_SIZE[size],
        className
      ),
      style: { backgroundColor: color },
      children: selected ? /* @__PURE__ */ jsx(Check, { className: "size-3.5", style: { color: fg } }) : null
    }
  );
}
function ColorPicker({
  value,
  onChange,
  palette = CATALOG_PALETTE.map((s) => s.hex),
  allowCustom = true,
  allowClear = true,
  disabled,
  placeholder = "Pick a colour",
  id,
  className
}) {
  const [open, setOpen] = React13.useState(false);
  const [draft, setDraft] = React13.useState(value ?? "");
  React13.useEffect(() => setDraft(value ?? ""), [value]);
  const normalized = normalizeHex(value);
  const commitCustom = () => {
    const next = normalizeHex(draft);
    if (next) {
      onChange(next);
      setOpen(false);
    }
  };
  return /* @__PURE__ */ jsxs(Popover, { open, onOpenChange: disabled ? void 0 : setOpen, children: [
    /* @__PURE__ */ jsxs(
      PopoverTrigger,
      {
        id,
        type: "button",
        disabled,
        className: cn(
          "inline-flex items-center gap-2 rounded-md border bg-background px-2.5 py-1.5 text-sm hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50",
          className
        ),
        children: [
          normalized ? /* @__PURE__ */ jsx("span", { className: "inline-block size-4 rounded-sm border border-black/10", style: { backgroundColor: normalized } }) : /* @__PURE__ */ jsx(Pipette, { className: "size-4 text-muted-foreground" }),
          /* @__PURE__ */ jsx("span", { className: cn(!normalized && "text-muted-foreground"), children: normalized ?? placeholder })
        ]
      }
    ),
    /* @__PURE__ */ jsxs(PopoverContent, { align: "start", className: "w-60 space-y-3", children: [
      /* @__PURE__ */ jsx("div", { className: "grid grid-cols-6 gap-2", children: palette.map((color) => /* @__PURE__ */ jsx(
        ColorSwatch,
        {
          color,
          selected: normalizeHex(color) === normalized,
          onSelect: () => {
            onChange(normalizeHex(color));
            setOpen(false);
          }
        },
        color
      )) }),
      allowCustom && /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx(
          Input,
          {
            "aria-label": "Custom hex colour",
            value: draft,
            placeholder: "#rrggbb",
            onChange: (e) => setDraft(e.target.value),
            onKeyDown: (e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitCustom();
              }
            },
            className: cn("h-8 font-mono text-xs", draft && !isHexColor(draft) && "border-destructive")
          }
        ),
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            onClick: commitCustom,
            disabled: !isHexColor(draft),
            className: "shrink-0 rounded-md border px-2 py-1 text-xs hover:bg-accent disabled:opacity-50",
            children: "Set"
          }
        )
      ] }),
      allowClear && normalized && /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          onClick: () => {
            onChange(null);
            setOpen(false);
          },
          className: "text-xs text-muted-foreground hover:text-foreground",
          children: "Clear colour"
        }
      )
    ] })
  ] });
}
var CURATED_EMOJIS = [
  { emoji: "\u{1F680}", keywords: "rocket launch ship startup" },
  { emoji: "\u2B50", keywords: "star favorite" },
  { emoji: "\u{1F525}", keywords: "fire hot trending" },
  { emoji: "\u{1F4A1}", keywords: "idea bulb light" },
  { emoji: "\u{1F6E1}\uFE0F", keywords: "shield security protect" },
  { emoji: "\u{1F512}", keywords: "lock secure private" },
  { emoji: "\u{1F511}", keywords: "key access" },
  { emoji: "\u{1F3E2}", keywords: "office building company organization" },
  { emoji: "\u{1F3E6}", keywords: "bank finance" },
  { emoji: "\u{1F4BC}", keywords: "briefcase work business" },
  { emoji: "\u{1F4CA}", keywords: "chart analytics report bar" },
  { emoji: "\u{1F4C8}", keywords: "growth chart up trending" },
  { emoji: "\u{1F310}", keywords: "globe web world network" },
  { emoji: "\u2699\uFE0F", keywords: "gear settings config" },
  { emoji: "\u{1F9E9}", keywords: "puzzle integration piece" },
  { emoji: "\u{1F517}", keywords: "link chain connect" },
  { emoji: "\u26D3\uFE0F", keywords: "chain blockchain links" },
  { emoji: "\u{1F48E}", keywords: "diamond gem crypto" },
  { emoji: "\u{1FA99}", keywords: "coin token money" },
  { emoji: "\u{1F4B0}", keywords: "money bag finance" },
  { emoji: "\u{1F91D}", keywords: "handshake deal partner team" },
  { emoji: "\u{1F465}", keywords: "people team group users" },
  { emoji: "\u{1F44B}", keywords: "wave hello welcome" },
  { emoji: "\u{1F3AF}", keywords: "target goal aim" },
  { emoji: "\u{1F3C6}", keywords: "trophy win award" },
  { emoji: "\u2705", keywords: "check done complete success" },
  { emoji: "\u{1F331}", keywords: "seedling grow new sprout" },
  { emoji: "\u{1F30D}", keywords: "earth globe region world" },
  { emoji: "\u{1F9E0}", keywords: "brain smart ai intelligence" },
  { emoji: "\u{1F916}", keywords: "robot bot automation ai" },
  { emoji: "\u{1F4E6}", keywords: "package box product ship" },
  { emoji: "\u{1F6E0}\uFE0F", keywords: "tools build dev" },
  { emoji: "\u26A1", keywords: "bolt fast speed power" },
  { emoji: "\u{1F50D}", keywords: "search magnify investigate" },
  { emoji: "\u{1F4C1}", keywords: "folder files project" },
  { emoji: "\u{1F3F7}\uFE0F", keywords: "tag label" },
  { emoji: "\u{1F3A8}", keywords: "palette design art brand" },
  { emoji: "\u{1F31F}", keywords: "glowing star sparkle premium" },
  { emoji: "\u2764\uFE0F", keywords: "heart love" },
  { emoji: "\u{1F600}", keywords: "smile happy face" }
];
function EmojiPicker({
  value,
  onSelect,
  placeholder = "\u{1F642}",
  ariaLabel = "Pick an emoji",
  emojis = CURATED_EMOJIS,
  disabled,
  className
}) {
  const [open, setOpen] = React13.useState(false);
  const [query, setQuery] = React13.useState("");
  const filtered = React13.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return emojis;
    return emojis.filter((e) => e.emoji === q || e.keywords.includes(q));
  }, [emojis, query]);
  return /* @__PURE__ */ jsxs(Popover, { open, onOpenChange: setOpen, children: [
    /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsx(
      Button,
      {
        type: "button",
        variant: "outline",
        size: "icon",
        disabled,
        "aria-label": ariaLabel,
        className: cn("text-xl", className),
        children: /* @__PURE__ */ jsx("span", { "aria-hidden": true, children: value || placeholder })
      }
    ) }),
    /* @__PURE__ */ jsxs(PopoverContent, { align: "start", className: "w-64 p-2", children: [
      /* @__PURE__ */ jsx(
        Input,
        {
          autoFocus: true,
          value: query,
          onChange: (e) => setQuery(e.target.value),
          placeholder: "Search emoji\u2026",
          className: "mb-2 h-8",
          "aria-label": "Search emoji"
        }
      ),
      /* @__PURE__ */ jsx(ScrollArea, { className: "h-44", children: filtered.length === 0 ? /* @__PURE__ */ jsx("p", { className: "px-1 py-6 text-center text-sm text-muted-foreground", children: "No matches" }) : /* @__PURE__ */ jsx("div", { className: "grid grid-cols-6 gap-1", children: filtered.map((entry) => /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          onClick: () => {
            onSelect(entry.emoji);
            setOpen(false);
            setQuery("");
          },
          className: cn(
            "flex size-9 items-center justify-center rounded-md text-xl transition-colors hover:bg-accent",
            value === entry.emoji && "bg-accent ring-2 ring-ring"
          ),
          "aria-label": entry.keywords.split(" ")[0],
          children: /* @__PURE__ */ jsx("span", { "aria-hidden": true, children: entry.emoji })
        },
        entry.emoji
      )) }) })
    ] })
  ] });
}
var QUICK_ACTION_CLASS = "quick-action";
var QUICK_ACTIONS_SCOPE = { "data-quick-actions-scope": "" };
var QuickActionsContext = React13.createContext({});
function QuickActionsProvider({
  actions,
  userActions,
  children
}) {
  const parent = React13.useContext(QuickActionsContext);
  const value = React13.useMemo(
    () => ({
      actions: actions ?? parent.actions,
      // Inherited unless a provider states it again: a surface default must not
      // be able to drop the reader's own choice by wrapping itself in a provider.
      userActions: userActions ?? parent.userActions
    }),
    [actions, userActions, parent.actions, parent.userActions]
  );
  return /* @__PURE__ */ jsx(QuickActionsContext.Provider, { value, children });
}
function resolveQuickActions(own, settings) {
  if (settings.userActions && settings.userActions !== "auto") return settings.userActions;
  return own ?? settings.actions ?? "auto";
}
function useQuickActions(own) {
  return resolveQuickActions(own, React13.useContext(QuickActionsContext));
}
function MiddleEllipsis({
  value,
  kind,
  className,
  title,
  monospace = true
}) {
  const { head, tail } = getTruncation(kind === "auto" ? truncationKindOf(value) : kind);
  const prefix = hexPrefixOf(value);
  const base = cn("text-sm", monospace && "font-mono", className);
  if (value.length - prefix.length <= head + tail + 1 || /\s/.test(value.trim())) {
    return /* @__PURE__ */ jsx("span", { className: base, title: title ?? value, children: value });
  }
  const headWidth = prefix.length + head + 1;
  return /* @__PURE__ */ jsx(
    "span",
    {
      className: cn("authe-middle-ellipsis", base),
      "data-tail": value.slice(-tail),
      style: { maxWidth: `min(100%, calc(${headWidth + tail}ch + 1px))` },
      title: title ?? value,
      children: /* @__PURE__ */ jsx("span", { className: "authe-middle-ellipsis__head", style: { maxWidth: `calc(${headWidth}ch + 1px)` }, children: value })
    }
  );
}
function opensInApp(event) {
  if (event.defaultPrevented) return false;
  if (event.button !== 0) return false;
  return !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey);
}
var NEW_TAB_HINT = "(opens in a new tab)";
var ICON_CLASS = {
  3: "size-3",
  3.5: "size-3.5",
  4: "size-4"
};
function ExternalLink({
  href,
  children,
  className,
  showIcon = true,
  iconSize = 3,
  hideNewTabHint = false,
  ...rest
}) {
  return /* @__PURE__ */ jsxs(
    "a",
    {
      ...rest,
      href,
      target: "_blank",
      rel: "noreferrer noopener",
      className: cn("inline-flex min-w-0 max-w-full items-center gap-1 hover:underline", className),
      children: [
        children,
        showIcon && /* @__PURE__ */ jsx(ExternalLink$1, { className: cn("shrink-0", ICON_CLASS[iconSize]), "aria-hidden": true }),
        !hideNewTabHint && /* @__PURE__ */ jsxs("span", { className: "sr-only", children: [
          " ",
          NEW_TAB_HINT
        ] })
      ]
    }
  );
}
function RichTooltip({
  children,
  title,
  subtitle,
  headerRight,
  rows,
  actions,
  content,
  align = "start",
  side = "top",
  openDelay = 150,
  closeDelay = 80,
  className,
  asChild = true
}) {
  const hasBody = Boolean(title || subtitle || rows && rows.length || content || actions && actions.length);
  return /* @__PURE__ */ jsxs(HoverCard, { openDelay, closeDelay, children: [
    /* @__PURE__ */ jsx(HoverCardTrigger, { asChild, children: asChild ? children : /* @__PURE__ */ jsx("span", { children }) }),
    hasBody && /* @__PURE__ */ jsxs(
      HoverCardContent,
      {
        align,
        side,
        className: cn("w-72 space-y-2 p-3 text-sm", className),
        children: [
          (title || subtitle || headerRight) && /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between gap-2", children: [
            /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
              title && /* @__PURE__ */ jsx("div", { className: "truncate font-medium leading-tight", children: title }),
              subtitle && /* @__PURE__ */ jsx("div", { className: "truncate text-xs text-muted-foreground", children: subtitle })
            ] }),
            headerRight && /* @__PURE__ */ jsx("div", { className: "shrink-0", children: headerRight })
          ] }),
          rows && rows.length > 0 && /* @__PURE__ */ jsx("dl", { className: "space-y-1", children: rows.map((row, i) => /* @__PURE__ */ jsxs("div", { className: "flex items-baseline justify-between gap-3", children: [
            /* @__PURE__ */ jsx("dt", { className: "shrink-0 text-xs text-muted-foreground", children: row.label }),
            /* @__PURE__ */ jsx("dd", { className: "min-w-0 truncate text-right text-xs font-medium", children: row.value })
          ] }, i)) }),
          content,
          actions && actions.length > 0 && /* @__PURE__ */ jsx("div", { className: "flex flex-wrap gap-1 border-t pt-2", children: actions.map((action) => {
            const Icon2 = action.icon;
            return /* @__PURE__ */ jsxs(
              "button",
              {
                type: "button",
                onClick: (e) => {
                  e.stopPropagation();
                  action.onClick();
                },
                "aria-label": action.label,
                title: action.label,
                className: "inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground",
                children: [
                  Icon2 && /* @__PURE__ */ jsx(Icon2, { className: "size-3.5" }),
                  !action.iconOnly && /* @__PURE__ */ jsx("span", { children: action.label })
                ]
              },
              action.label
            );
          }) })
        ]
      }
    )
  ] });
}
var CLAMP_CLASS = {
  1: "line-clamp-1",
  2: "line-clamp-2",
  3: "line-clamp-3",
  4: "line-clamp-4",
  5: "line-clamp-5",
  6: "line-clamp-6"
};
function ExpandableText({
  text,
  lines = 2,
  monospace = false,
  toggleable = true,
  onExpandToFull,
  expandLabel = "Show more",
  collapseLabel = "Show less",
  openLabel = "Open",
  className
}) {
  const [expanded, setExpanded] = React13.useState(false);
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-1", className), children: [
    /* @__PURE__ */ jsx(
      "p",
      {
        title: text,
        className: cn(
          "whitespace-pre-wrap break-words text-sm",
          monospace && "font-mono text-xs",
          !expanded && CLAMP_CLASS[lines]
        ),
        children: text
      }
    ),
    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
      toggleable && /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          onClick: () => setExpanded((v) => !v),
          className: "text-xs font-medium text-primary hover:underline",
          children: expanded ? collapseLabel : expandLabel
        }
      ),
      onExpandToFull && /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          onClick: () => onExpandToFull(text),
          className: "text-xs font-medium text-muted-foreground hover:text-foreground hover:underline",
          children: openLabel
        }
      )
    ] })
  ] });
}
var TREND_ICON = {
  up: ArrowUpRight,
  down: ArrowDownRight,
  flat: Minus
};
var TREND_COLOR = {
  up: "text-finality-finalized",
  down: "text-risk-high",
  flat: "text-muted-foreground"
};
var PAD = {
  sm: "p-3",
  md: "p-4"
};
function DataCard({
  label,
  value,
  secondary,
  icon,
  trailing,
  mono,
  trend,
  delta,
  loading,
  onClick,
  size = "md",
  className
}) {
  const interactive = Boolean(onClick);
  const TrendIcon = trend ? TREND_ICON[trend] : null;
  const body = loading ? /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
    /* @__PURE__ */ jsx(Skeleton, { className: "h-3 w-16" }),
    /* @__PURE__ */ jsx(Skeleton, { className: "h-5 w-24" })
  ] }) : /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-3", children: [
    icon && /* @__PURE__ */ jsx("div", { className: "mt-0.5 shrink-0 text-muted-foreground", children: icon }),
    /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1", children: [
      /* @__PURE__ */ jsx("div", { className: "truncate text-2xs font-medium uppercase tracking-wide text-muted-foreground", children: label }),
      /* @__PURE__ */ jsxs("div", { className: "mt-0.5 flex items-baseline gap-1.5", children: [
        /* @__PURE__ */ jsx("span", { className: cn("truncate text-base font-semibold leading-tight", mono && "font-mono tabular-nums"), children: value }),
        trend && TrendIcon && /* @__PURE__ */ jsxs("span", { className: cn("inline-flex items-center gap-0.5 text-xs font-medium", TREND_COLOR[trend]), children: [
          /* @__PURE__ */ jsx(TrendIcon, { className: "size-3" }),
          delta
        ] })
      ] }),
      secondary != null && /* @__PURE__ */ jsx("div", { className: cn("mt-0.5 truncate text-xs text-muted-foreground", mono && "font-mono"), children: secondary })
    ] }),
    trailing && /* @__PURE__ */ jsx("div", { className: "shrink-0", children: trailing })
  ] });
  return /* @__PURE__ */ jsx(
    Card,
    {
      className: cn(
        PAD[size],
        interactive && "cursor-pointer transition-colors hover:border-ring hover:bg-accent/40 focus-ring",
        className
      ),
      ...interactive ? {
        role: "button",
        tabIndex: 0,
        onClick,
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick?.();
          }
        }
      } : {},
      children: body
    }
  );
}
function LatestReleaseBadge({
  summary,
  onSelect,
  className,
  label = "Latest release"
}) {
  if (!summary) return null;
  const title = `This session runs the latest release of: ${summary}. It is not yet live for everyone.`;
  const badge = /* @__PURE__ */ jsx(Badge, { variant: "brand", className: cn("whitespace-nowrap", className), title, children: label });
  if (!onSelect) return badge;
  return /* @__PURE__ */ jsx(
    "button",
    {
      type: "button",
      onClick: onSelect,
      title,
      "aria-label": `${label}: ${summary}. Open release details.`,
      className: "rounded-full focus-ring",
      children: badge
    }
  );
}

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Alert, AlertDescription, AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay, AlertDialogPortal, AlertDialogTitle, AlertDialogTrigger, AlertTitle, Avatar, Breadcrumb, BreadcrumbEllipsis, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator, CURATED_EMOJIS, Calendar, Collapsible, CollapsibleContent2 as CollapsibleContent, CollapsibleTrigger2 as CollapsibleTrigger, ColorPicker, ColorSwatch, DataCard, DatePicker, DateRangePicker, EmojiPicker, EnumSelect, ExpandableText, ExternalLink, FilterChip, HoverCard, HoverCardContent, HoverCardTrigger, LatestReleaseBadge, MiddleEllipsis, NEW_TAB_HINT, Progress, QUICK_ACTIONS_SCOPE, QUICK_ACTION_CLASS, QuickActionsProvider, RadioGroup, RadioGroupItem, RichTooltip, ScrollArea, ScrollBar, Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue, Slider, Switch, ViewModeToggle, VirtualList, absoluteRangeFrom, alertVariants, isWholeDayRange, opensInApp, resolveQuickActions, useQuickActions, withTimeOfDay };
//# sourceMappingURL=chunk-TU2ZUVHW.js.map
//# sourceMappingURL=chunk-TU2ZUVHW.js.map